import { ImportedListing } from '../../_types/imported-listing'
import { QuickPassUnitPreferences } from '../../_types/quickpass-profile'
import { computeMatchScore } from '../match-score-badge/helpers'
import { CompareRow } from './types'

const formatPrice = (priceMonthly: number | null): string => {
  if (priceMonthly === null) {
    return '—'
  }
  return `$${priceMonthly.toLocaleString('en-CA')}/mo`
}

const formatNumberOrDash = (value: number | null): string => {
  if (value === null) {
    return '—'
  }
  return value.toString()
}

const formatPropertyType = (
  propertyType: ImportedListing['propertyType'],
): string => {
  if (propertyType === null) {
    return '—'
  }
  return `${propertyType[0].toUpperCase()}${propertyType.slice(1)}`
}

const formatAvailability = (isAvailable: boolean | null): string => {
  if (isAvailable === true) {
    return 'Available'
  }
  if (isAvailable === false) {
    return 'Unavailable'
  }
  return 'Unknown'
}

/**
 * Builds the side-by-side comparison rows for two imported listings,
 * marking which side wins each axis. Wins use simple semantics: lower
 * price wins, higher match wins, "Available" beats anything else.
 *
 * @param {ImportedListing} alpha - the left listing
 * @param {ImportedListing} beta - the right listing
 * @param {QuickPassUnitPreferences | null} preferences - for match scores
 * @returns {CompareRow[]} ordered comparison rows
 */
const buildCompareRows = (
  alpha: ImportedListing,
  beta: ImportedListing,
  preferences: QuickPassUnitPreferences | null,
): CompareRow[] => {
  const rows: CompareRow[] = []

  const alphaScore = computeMatchScore(alpha, preferences).score
  const betaScore = computeMatchScore(beta, preferences).score

  rows.push({
    label: 'Match score',
    alphaValue: `${alphaScore}%`,
    betaValue: `${betaScore}%`,
    alphaWins: alphaScore > betaScore,
    betaWins: betaScore > alphaScore,
  })

  const alphaPrice = alpha.priceMonthly ?? Number.POSITIVE_INFINITY
  const betaPrice = beta.priceMonthly ?? Number.POSITIVE_INFINITY
  rows.push({
    label: 'Price',
    alphaValue: formatPrice(alpha.priceMonthly),
    betaValue: formatPrice(beta.priceMonthly),
    alphaWins:
      alpha.priceMonthly !== null &&
      beta.priceMonthly !== null &&
      alphaPrice < betaPrice,
    betaWins:
      alpha.priceMonthly !== null &&
      beta.priceMonthly !== null &&
      betaPrice < alphaPrice,
  })

  rows.push({
    label: 'Bedrooms',
    alphaValue: formatNumberOrDash(alpha.bedrooms),
    betaValue: formatNumberOrDash(beta.bedrooms),
    alphaWins: false,
    betaWins: false,
  })

  rows.push({
    label: 'Bathrooms',
    alphaValue: formatNumberOrDash(alpha.bathrooms),
    betaValue: formatNumberOrDash(beta.bathrooms),
    alphaWins: false,
    betaWins: false,
  })

  rows.push({
    label: 'Place type',
    alphaValue: formatPropertyType(alpha.propertyType),
    betaValue: formatPropertyType(beta.propertyType),
    alphaWins: false,
    betaWins: false,
  })

  rows.push({
    label: 'City',
    alphaValue: alpha.city || '—',
    betaValue: beta.city || '—',
    alphaWins: false,
    betaWins: false,
  })

  rows.push({
    label: 'Availability',
    alphaValue: formatAvailability(alpha.isAvailable),
    betaValue: formatAvailability(beta.isAvailable),
    alphaWins: alpha.isAvailable === true && beta.isAvailable !== true,
    betaWins: beta.isAvailable === true && alpha.isAvailable !== true,
  })

  return rows
}

export { buildCompareRows }
